/**
 * Single product page — visual controls for WooCommerce variation selects.
 *
 * WooCommerce remains the source of truth: swatches only update its real
 * selects, then trigger the same change event a customer would create in a
 * native dropdown. Availability is calculated from the complete variation
 * attribute matrix so extra attributes cannot produce invalid combinations.
 */
export function initVariationSwatches() {
	document.querySelectorAll( '.pdp-variations-form' ).forEach( initForm );
}

function initForm( form ) {
	const groups = [ ...form.querySelectorAll( '[data-swatch-for]' ) ];
	if ( ! groups.length ) return;

	const selects = [ ...form.querySelectorAll( 'select[name^="attribute_"]' ) ];
	let variations = [];
	const getSelect = ( group ) => selects.find( ( select ) => select.name === group.dataset.swatchFor );
	const getButtonValue = ( button ) => button.dataset.color ?? button.dataset.size ?? '';
	const setSelectValue = ( select, value ) => {
		const option = [ ...select.options ].find( ( item ) => item.value === value );

		if ( ! option ) return false;

		select.value = option.value;

		return true;
	};

	const getSelections = ( overrideName = '', overrideValue = '' ) => Object.fromEntries(
		selects.map( ( select ) => [
			select.name,
			select.name === overrideName ? overrideValue : select.value,
		] )
	);

	const isAvailable = ( selections ) => variations.some( ( variation ) => {
		if ( variation.is_in_stock === false || variation.is_purchasable === false ) return false;

		return Object.entries( selections ).every( ( [ attribute, selectedValue ] ) => {
			if ( ! selectedValue ) return true;

			const variationValue = variation.attributes?.[ attribute ] ?? '';

			return variationValue === '' || variationValue === selectedValue;
		} );
	} );

	const syncSelectionState = () => {
		groups.forEach( ( group ) => {
			const select = getSelect( group );
			if ( ! select ) return;

			group.querySelectorAll( 'button[data-color], button[data-size]' ).forEach( ( button ) => {
				const value       = button.dataset.color ?? button.dataset.size ?? '';
				const activeClass = button.dataset.color ? 'is-active' : 'is-selected';

				button.classList.toggle( activeClass, value === select.value && select.value !== '' );

				if ( variations.length ) {
					button.classList.toggle( 'is-unavailable', ! isAvailable( getSelections( select.name, value ) ) );
				}
			} );
		} );
	};

	const triggerSelectChange = ( select ) => {
		if ( typeof window.jQuery === 'function' ) {
			window.jQuery( select ).trigger( 'change' );
			return;
		}

		select.dispatchEvent( new Event( 'change', { bubbles: true } ) );
	};

	groups.forEach( ( group ) => {
		const select = getSelect( group );
		if ( ! select ) return;

		group.querySelectorAll( 'button[data-color], button[data-size]' ).forEach( ( button ) => {
			button.addEventListener( 'click', () => {
				if ( button.classList.contains( 'is-unavailable' ) ) return;

				if ( ! setSelectValue( select, getButtonValue( button ) ) ) return;

				triggerSelectChange( select );
				syncSelectionState();
			} );
		} );
	} );

	// Ensure a click on a visual control is represented by its matching native
	// select in the POST body, even if WooCommerce initialises after this script.
	form.addEventListener( 'submit', () => {
		groups.forEach( ( group ) => {
			const select = getSelect( group );
			const button = group.querySelector( '.is-active, .is-selected' );

			if ( select && button ) {
				setSelectValue( select, getButtonValue( button ) );
			}
		} );
	} );

	selects.forEach( ( select ) => select.addEventListener( 'change', syncSelectionState ) );

	if ( typeof window.jQuery === 'function' ) {
		window.jQuery( form ).on(
			'change woocommerce_variation_has_changed found_variation hide_variation reset_data',
			syncSelectionState
		);
	}

	variations = parseVariations( form );
	syncSelectionState();
}

function parseVariations( form ) {
	const raw = form.getAttribute( 'data-product_variations' );
	if ( ! raw || raw === 'false' ) return [];

	const variations = JSON.parse( raw );

	return Array.isArray( variations ) ? variations : [];
}
